import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import {
  PlayCircle, RefreshCw, Clock, ExternalLink, ArrowDownNarrowWide, X, Youtube,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPublished(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

function relativeTime(iso) {
  if (!iso) return "";
  const now = Date.now();
  const t = Date.parse(iso);
  if (isNaN(t)) return "";
  const diffMin = Math.round((now - t) / 60000);
  if (diffMin < 60) return `vor ${Math.max(1, diffMin)} Min.`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std.`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `vor ${diffD} Tg.`;
  if (diffD < 30) return `vor ${Math.round(diffD / 7)} Wo.`;
  const diffM = Math.round(diffD / 30);
  return `vor ${diffM} Mon.`;
}

function VideoCard({ video, onPlay }) {
  const [imgFailed, setImgFailed] = useState(false);
  const src = imgFailed ? video.fallback_image_url : video.image_url;
  return (
    <button
      onClick={() => onPlay(video)}
      data-testid={`video-card-${video.video_id}`}
      className="group text-left bg-white/[0.015] border border-white/[0.06] hover:border-[#00e5ff]/40 hover:bg-white/[0.03] transition-all duration-200 flex flex-col h-full"
    >
      <div className="relative overflow-hidden aspect-video bg-white/[0.02]">
        {src ? (
          <img
            src={src}
            alt={video.title}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Youtube size={40} className="text-white/20" />
          </div>
        )}
        {/* Channel badge */}
        <div
          className="absolute top-3 left-3 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-outfit font-semibold backdrop-blur-sm"
          style={{
            background: `${video.color}18`,
            color: video.color,
            border: `1px solid ${video.color}55`,
          }}
        >
          {video.channel_name}
        </div>
        {/* Play overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
        <div className="absolute inset-0 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 bg-black/70 border border-white/20 group-hover:bg-[#00e5ff]/20 group-hover:border-[#00e5ff] flex items-center justify-center transition-colors">
            <PlayCircle size={28} className="text-white group-hover:text-[#00e5ff] transition-colors" strokeWidth={1.4} />
          </div>
        </div>
        {/* Focus tag */}
        <div className="absolute bottom-3 right-3 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.2em] font-outfit font-bold bg-black/75 text-white/80 border border-white/15 backdrop-blur-sm">
          {video.focus}
        </div>
      </div>
      <div className="flex-1 flex flex-col p-5">
        <h3 className="font-outfit font-bold text-[15px] text-white leading-snug tracking-tight mb-2 line-clamp-2 group-hover:text-[#00e5ff] transition-colors">
          {video.title}
        </h3>
        {video.description && (
          <p className="text-[13px] text-white/55 leading-relaxed mb-3 line-clamp-2">
            {video.description}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between text-[11.5px] text-white/40 font-outfit pt-3 border-t border-white/[0.05]">
          <span className="inline-flex items-center gap-1.5">
            <Clock size={11} />
            {formatPublished(video.published_at)}
            <span className="text-white/25">· {relativeTime(video.published_at)}</span>
          </span>
          <span className="inline-flex items-center gap-1 text-[#00e5ff]/70">
            Ansehen <ExternalLink size={10} />
          </span>
        </div>
      </div>
    </button>
  );
}

function VideoModal({ video, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!video) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-modal"
    >
      <div
        className="relative w-full max-w-4xl bg-[#0a0a0a] border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="video-modal-close"
          className="absolute -top-3 -right-3 w-8 h-8 border border-white/20 bg-black hover:bg-[#00e5ff]/20 hover:border-[#00e5ff] flex items-center justify-center transition-colors z-10"
        >
          <X size={14} className="text-white" />
        </button>
        <div className="aspect-video bg-black">
          <iframe
            title={video.title}
            src={`https://www.youtube-nocookie.com/embed/${video.video_id}?autoplay=1&rel=0&modestbranding=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            className="w-full h-full border-0"
          />
        </div>
        <div className="p-5 border-t border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-outfit font-semibold"
              style={{ background: `${video.color}18`, color: video.color, border: `1px solid ${video.color}55` }}
            >
              {video.channel_name}
            </span>
            <span className="text-[11px] text-white/40 font-outfit">
              {formatPublished(video.published_at)} · {relativeTime(video.published_at)}
            </span>
          </div>
          <h2 className="font-outfit font-bold text-white text-[17px] tracking-tight mb-2">{video.title}</h2>
          {video.description && (
            <p className="text-[13px] text-white/60 leading-relaxed whitespace-pre-wrap line-clamp-6">
              {video.description}
            </p>
          )}
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-modal-youtube-link"
            className="inline-flex items-center gap-1.5 mt-4 text-[#00e5ff] text-[12px] font-outfit font-semibold uppercase tracking-[0.14em] hover:underline"
          >
            Auf YouTube öffnen <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

export default function VideosPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/videos/`, { withCredentials: true });
      setItems(data.items || []);
      setChannels(data.channels || []);
    } catch (e) {
      console.error("videos load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const manualRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/videos/refresh`, {}, { withCredentials: true });
      await load();
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const o = { all: items.length };
    for (const v of items) o[v.channel_handle] = (o[v.channel_handle] || 0) + 1;
    return o;
  }, [items]);

  const filtered = useMemo(() => {
    const base = activeChannel === "all" ? items : items.filter((v) => v.channel_handle === activeChannel);
    const ts = (x) => {
      const t = Date.parse(x.published_at || x.fetched_at || "");
      return isNaN(t) ? 0 : t;
    };
    const copy = [...base];
    if (sortBy === "newest") copy.sort((a, b) => ts(b) - ts(a));
    else if (sortBy === "oldest") copy.sort((a, b) => ts(a) - ts(b));
    else if (sortBy === "channel") copy.sort((a, b) => (a.channel_name || "").localeCompare(b.channel_name || "") || ts(b) - ts(a));
    return copy;
  }, [items, activeChannel, sortBy]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px bg-[#00e5ff]" />
              <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">
                KI-Videos (automatisch)
              </span>
            </div>
            <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white" data-testid="videos-heading">
              KI-<span className="text-[#00e5ff]">YouTube</span>-Kanäle
            </h1>
            <p className="text-base text-white/55 mt-3 max-w-2xl leading-relaxed">
              Kuratierte YouTube-Kanäle — {channels.map((c) => c.name).join(", ")}. Neue Videos erscheinen automatisch, neueste zuerst. Ein Klick öffnet das Video direkt hier.
            </p>
          </div>
          {user?.role === "admin" && (
            <button
              onClick={manualRefresh}
              disabled={refreshing}
              data-testid="videos-refresh-btn"
              className="inline-flex items-center gap-2 px-4 h-11 border border-white/15 hover:border-[#00e5ff]/40 hover:text-[#00e5ff] text-white/70 rounded-none font-outfit text-sm transition-colors"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Aktualisiere…" : "Jetzt aktualisieren"}
            </button>
          )}
        </div>

        <div className="mb-6 text-[12px] text-white/40 border-l-2 border-[#00e5ff]/30 pl-3 py-1 bg-white/[0.015]">
          Titel, Thumbnails und Beschreibungen stammen direkt von YouTube. [KI]NPUT zeigt nur eine kuratierte Übersicht. Für das vollständige Video öffnet sich der offizielle YouTube-Player.
        </div>
        <div className="mb-8 inline-flex items-start gap-2 px-3 py-2 border border-[#00e5ff]/25 bg-[#00e5ff]/[0.04] text-[12px]" data-testid="videos-no-ads-disclaimer">
          <span className="shrink-0 mt-0.5 text-[#00e5ff]/90 font-outfit font-bold text-[10px] uppercase tracking-[0.18em]">Unabhängig</span>
          <span className="text-white/70 leading-relaxed">
            Keine Werbepartnerschaft, keine bezahlten Platzierungen. Wir empfehlen diese Kanäle, weil wir die Inhalte qualitativ gut und relevant finden.
          </span>
        </div>

        {/* Channel filter + Sort */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="videos-filter">
          <button
            onClick={() => setActiveChannel("all")}
            data-testid="videos-filter-all"
            className={`px-3.5 py-2 text-[13px] font-outfit tracking-wide transition-all border ${activeChannel === "all" ? "bg-[#00e5ff] text-black border-[#00e5ff] font-semibold" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"}`}
          >
            Alle Kanäle <span className="text-[11px] opacity-60">({counts.all || 0})</span>
          </button>
          {channels.map((c) => {
            const active = activeChannel === c.handle;
            return (
              <button
                key={c.handle}
                onClick={() => setActiveChannel(c.handle)}
                data-testid={`videos-filter-${c.handle}`}
                className={`px-3.5 py-2 text-[13px] font-outfit tracking-wide transition-all border ${active ? "font-semibold" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"}`}
                style={active ? { color: c.color, background: `${c.color}14`, borderColor: `${c.color}80` } : {}}
              >
                {c.name} <span className="text-[11px] opacity-60">({counts[c.handle] || 0})</span>
              </button>
            );
          })}
          <div className="flex-1" />
          <div className="inline-flex items-center gap-2 border border-white/10 bg-black px-2.5 h-10 focus-within:border-[#00e5ff]/50 transition-colors">
            <ArrowDownNarrowWide size={13} className="text-white/40" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="videos-sort"
              className="bg-transparent text-[12.5px] font-outfit text-white/80 focus:outline-none appearance-none cursor-pointer pr-1"
            >
              <option value="newest" className="bg-[#0a0a0a]">Neueste zuerst</option>
              <option value="oldest" className="bg-[#0a0a0a]">Älteste zuerst</option>
              <option value="channel" className="bg-[#0a0a0a]">Nach Kanal</option>
            </select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <div key={`skel-vid-${i}`} className="aspect-[4/3] border border-white/[0.05] bg-white/[0.015] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center border border-white/[0.06] bg-white/[0.015]" data-testid="videos-empty">
            <PlayCircle size={32} className="text-white/25 mx-auto mb-3" strokeWidth={1.3} />
            <p className="text-white/70 font-outfit font-semibold text-lg">Noch keine Videos geladen</p>
            <p className="text-[13px] text-white/40 mt-1.5">Der Crawler läuft alle 30 Min. automatisch.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-grid">
            {filtered.map((v) => (
              <VideoCard key={v.video_id} video={v} onPlay={setSelected} />
            ))}
          </div>
        )}
      </main>

      <VideoModal video={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
